import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";

import { Route as AppOnboardingRoute } from "@/routes/_app/_auth/onboarding/_layout.app_onboarding";
import { Button } from "@/components/ui/button";
import { Route as GrowRoute } from "@/routes/_app/_auth/grow";
import { LogOut } from "lucide-react";
import { ONBOARDING_EVENTS } from "@/constants/analyticsEvents";
import { Route as PartnershipsApplyRoute } from "@/routes/_app/_auth/dashboard/_layout.partnerships.apply";
import { api } from "@cvx/_generated/api";
import { useAccount } from "@/state/useAccount";
import { useMixpanel } from "@/hooks/useMixpanel";
import { useSignOut } from "@/utils/misc";
import { useState } from "react";

export const Route = createFileRoute("/_app/_auth/onboarding/_layout/usertype")(
  {
    component: OnboardingUserType,
    beforeLoad: () => ({
      title: `I am a...`,
    }),
  },
);

export default function OnboardingUserType() {
  const { data: user } = useQuery(convexQuery(api.app.getCurrentUser, {}));
  const [selectedType, setSelectedType] = useState<"creator" | "appFounder">();
  const navigate = useNavigate();
  const signOut = useSignOut();
  const { pendingCampaignId } = useAccount();
  const { mutateAsync: createCreator } = useMutation({
    mutationFn: useConvexMutation(api.creators.createCreator),
  });
  const { track } = useMixpanel();

  const handleContinue = async () => {
    if (!selectedType) return;

    // Track user type selection
    track(ONBOARDING_EVENTS.USER_TYPE_SELECTED, {
      user_id: user?._id,
      user_type: selectedType,
      has_pending_campaign: !!pendingCampaignId,
      timestamp: new Date().toISOString(),
    });

    if (selectedType === "creator") {
      await createCreator({});

      // Track creator profile creation
      track(ONBOARDING_EVENTS.CREATOR_PROFILE_CREATED, {
        user_id: user?._id,
        has_pending_campaign: !!pendingCampaignId,
        timestamp: new Date().toISOString(),
      });

      // Check if there's a pending campaign ID to apply
      if (pendingCampaignId) {
        navigate({
          to: PartnershipsApplyRoute.fullPath,
          search: { selectedCampaignId: pendingCampaignId },
        });
      } else {
        //   navigate({ to: PartnershipsApplyRoute.fullPath });
        navigate({ to: GrowRoute.fullPath });
      }
    } else {
      navigate({ to: AppOnboardingRoute.fullPath });
    }
  };

  return (
    <div className="mx-auto flex h-full w-full max-w-2xl flex-col items-center justify-center gap-6">
      <div className="flex flex-col items-center gap-2">
        <h3 className="text-center text-2xl font-medium text-primary">
          What brings you here?
        </h3>
        <p className="text-center text-base font-normal text-primary/60">
          Choose your role to get started
        </p>
      </div>

      <div className={`grid w-full gap-4`}>
        {!user?.creator && (
          <button
            onClick={() => setSelectedType("creator")}
            className={`flex flex-col items-center gap-4 rounded-lg border p-6 transition-all hover:border-primary/20 hover:bg-primary/5 ${
              selectedType === "creator"
                ? "border-primary bg-primary/10"
                : "border-border"
            }`}
          >
            <span className="text-4xl">🎥</span>
            <div className="flex flex-col items-center gap-1">
              <h4 className="text-lg font-medium text-primary">Creator</h4>
              <p className="text-center text-sm text-primary/60">
                I create content and want to collaborate with apps
              </p>
            </div>
          </button>
        )}

        {/* <button
          onClick={() => setSelectedType("appFounder")}
          className={`flex flex-col items-center gap-4 rounded-lg border p-6 transition-all hover:border-primary/20 hover:bg-primary/5 ${
            selectedType === "appFounder"
              ? "border-primary bg-primary/10"
              : "border-border"
          }`}
        >
          <span className="text-4xl">💡</span>
          <div className="flex flex-col items-center gap-1">
            <h4 className="text-lg font-medium text-primary">App Project</h4>
            <p className="text-center text-sm text-primary/60">
              I have an app and want to work with creators
            </p>
          </div>
        </button> */}
      </div>

      <button
        onClick={handleContinue}
        disabled={!selectedType}
        className={`w-full max-w-96 rounded-md px-4 py-2 text-sm font-medium transition-all ${
          selectedType
            ? "bg-primary text-primary-foreground hover:bg-primary/90"
            : "bg-primary/10 text-primary/40 cursor-not-allowed"
        }`}
      >
        Continue
      </button>

      <p className="px-6 text-center text-sm font-normal leading-normal text-primary/60">
        Don't worry, you can always add more accounts later from settings.
      </p>

      <Button
        onClick={() => {
          signOut();
        }}
        variant="link"
        className="flex items-center gap-2"
      >
        <LogOut className="h-4 w-4 stroke-[1.5px]" />
        Log out
      </Button>
    </div>
  );
}
